<template>
  <div>
    <v-card :elevation="0">
      <div class="header-container">
        <div>
          <Upload
            @add="onAddFile"
            container="profilbilder"
            :hide-container="true"
            :add-timestamp="true"
            accept="image/*"
          >
            <div
              v-if="user.admin_profile_image"
              class="profile-image"
              :style="profileImageStyleUrl"
            ></div>
            <v-btn v-else icon x-large style="background: lightblue">
              <v-icon>mdi-account</v-icon>
            </v-btn>
          </Upload>
        </div>
        <div>
          <v-card-title>
            {{ user.admin_name }}
          </v-card-title>
          <v-card-subtitle v-if="$route.name === 'RedaktorerHome'">{{ lastSeen }}</v-card-subtitle>
        </div>
      </div>
      <v-card-text>
        <v-text-field v-model="user.admin_name" label="Namn"></v-text-field>
        <v-text-field v-model="user.admin_email" label="Email"></v-text-field>
        <v-text-field v-model="user.admin_phone" label="Telefon"></v-text-field>
        <v-text-field v-model="user.admin_cellphone" label="Mobiltelefon"></v-text-field>
        <v-select
          v-if="isAdminOrDeveloper"
          :items="userRoleOptions"
          v-model="user.admin_role"
          label="Roll"
          hide-details
          item-text="title"
          item-value="name"
        />
      </v-card-text>
      <v-card-actions>
        <v-btn
          v-if="isAdminOrDeveloper && isNotViewingOwnProfile"
          color="red"
          text
          @click="onRemove"
        >
          Ta bort användare
        </v-btn>
        <v-btn color="primary" text @click="changePasswordDialog = true"> Ändra lösenord </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="primary" :loading="loading" @click="onSave">
          <v-icon left dark>check</v-icon>
          Spara
        </v-btn>
      </v-card-actions>
    </v-card>
    <component
      :is="selectChangePasswordDialog"
      :visible="changePasswordDialog"
      @close="changePasswordDialog = false"
    />
    <Confirm ref="confirm" />
    <Message ref="message" />
  </div>
</template>

<script>
import { dateParse } from '../../lib/misc'
import { mapGetters, mapActions } from 'vuex'
import { userRoleOptions } from '@/assets/data/options'
import { ADMIN, DEVELOPER } from '@/assets/data/userRoles'
import UserChangePassword from '@/components/users/UserChangePassword'
import UserSetPassword from '@/components/users/UserSetPassword'
import Message from '@/components/shared/Message'
import Confirm from '@/components/shared/Confirm'
import Upload from '@/components/shared/Upload.vue'

export default {
  name: 'UserSettings',
  props: {
    id: {
      type: [String, Number],
      required: false,
    },
  },
  components: {
    UserChangePassword,
    Message,
    Confirm,
    Upload,
  },
  data() {
    return {
      isInitialized: false,
      currentUserId: null,
      loading: false,
      userRoleOptions,
      changePasswordDialog: false,
    }
  },
  computed: {
    ...mapGetters('user', { getById: 'getById' }),
    ...mapGetters('auth', {
      getCurrentUserId: 'currentUserId',
      currentUserRole: 'currentUserRole',
    }),
    profileImageStyleUrl() {
      return `background-image:url('${this.user.admin_profile_image}')`
    },
    user: {
      get() {
        return this.getById(this.currentUserId)
      },
    },
    lastSeen() {
      return this.user.admin_last_seen ? `Sågs senast ${dateParse(this.user.admin_last_seen)}` : ''
    },
    isAdminOrDeveloper() {
      return this.currentUserRole === ADMIN || this.currentUserRole === DEVELOPER
    },
    isNotViewingOwnProfile() {
      return this.currentUserId !== this.getCurrentUserId
    },
    selectChangePasswordDialog() {
      return this.isAdminOrDeveloper && this.isNotViewingOwnProfile
        ? UserSetPassword
        : UserChangePassword
    },
  },
  watch: {
    $route: {
      handler({ params }) {
        if (params && params.id && params.id !== this.getCurrentUserId) {
          this.currentUserId = params.id
        } else {
          this.currentUserId = this.getCurrentUserId
        }
        this.fetch()
      },
      deep: true,
      immediate: true,
    },
  },

  methods: {
    ...mapActions('user', { getSingleUser: 'read', updateUser: 'update', removeUser: 'delete' }),
    async fetch() {
      this.isInitialized = false
      await this.getSingleUser({ id: this.currentUserId })
      this.isInitialized = true
    },
    async onAddFile({ azureFile }) {
      this.uploadFile = azureFile
      const { status, url } = await this.uploadFile.send()
      if (status === 201) {
        const updatedUser = { ...this.user, admin_profile_image: url }
        this.loading = true
        await this.updateUser(updatedUser)
        await this.getSingleUser({ id: this.currentUserId })
        this.$refs.message.open('Användaren har sparats')
        this.loading = false
      } else {
        // eslint-disable-next-line no-alert
        alert('Problem uppstod när filen skulle laddas upp')
      }
      this.uploadFile = null
    },
    async onSave() {
      this.loading = true
      await this.updateUser(this.user)
      this.$refs.message.open('Användaren har sparats')
      this.loading = false
    },
    async onRemove() {
      const shouldRemove = await this.$refs.confirm.open({
        title: 'Ta bort användaren',
        message: 'Är du säker på att du vill ta bort användaren?',
        saveButtonText: 'Ja',
        cancelButtonText: 'Avbryt',
      })
      if (!shouldRemove) return
      this.loading = true
      await this.removeUser(this.user)
      this.$refs.message.open('Användaren har tagits bort')
      this.$emit('update')
      this.loading = false
      this.$router.push({ name: 'RedaktorerHome' })
    },
  },
  mounted() {
    this.fetch()
  },
  beforeDestroy() {
    // BeforeDestroy
  },
}
</script>

<style lang="less" scoped>
.header-container {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-left: 15px;
}
.profile-image {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  cursor: pointer;
  display: inline-block;
}
</style>
